<script setup lang="ts">
const { t } = useI18n()

// Countdown timer logic
const now = ref(new Date())
const countdown = ref('')

// Get shipping cut-off time based on user's local timezone
function getCutoffTime() {
  const userDate = new Date()
  // If it's after 4pm, show the cutoff for tomorrow
  if (userDate.getHours() >= 16) {
    userDate.setDate(userDate.getDate() + 1)
  }

  // Set cutoff to 4pm (16:00)
  userDate.setHours(16, 0, 0, 0)
  return userDate
}

const cutoffTime = ref(getCutoffTime())

// Format time difference as HH:MM:SS
function formatTimeDiff(targetDate: Date, currentDate: Date) {
  const diff = targetDate.getTime() - currentDate.getTime()
  if (diff <= 0)
    return '00:00:00'

  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((diff % (1000 * 60)) / 1000)

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

// Update countdown every second for maximum urgency
let timer: NodeJS.Timeout | null = null

onMounted(() => {
  const updateCountdown = () => {
    now.value = new Date()
    countdown.value = formatTimeDiff(cutoffTime.value, now.value)
  }

  updateCountdown()
  timer = setInterval(updateCountdown, 1000) // Update every second
})

onBeforeUnmount(() => {
  if (timer)
    clearInterval(timer)
})

const countdownHTML = computed(() => {
  return t('product.order_countdown.message', [
    `<span class="relative inline-block animate-pulse text-red-600 font-bold tabular-nums">${countdown.value}</span>`,
  ])
})
</script>

<template>
  <div class="flex items-center">
    <div class="flex items-center rounded bg-amber-100 px-2.5 py-1.5">
      <div class="flex items-center">
        <Icon name="lucide:hourglass" class="animate-pulse text-lg text-amber-600" />
        <p class="ml-2 text-sm text-amber-700 font-semibold" v-html="countdownHTML" />
      </div>
    </div>

    <LazyUiTooltipProvider>
      <LazyUiTooltip>
        <LazyUiTooltipTrigger>
          <Icon name="i-mdi:information-variant-circle" class="ml-1 text-base text-muted-foreground/50" />
        </LazyUiTooltipTrigger>
        <LazyUiTooltipContent>
          <div class="max-w-xs">
            {{ t('product.order_countdown.tooltip') }}
          </div>
        </LazyUiTooltipContent>
      </LazyUiTooltip>
    </LazyUiTooltipProvider>
  </div>
</template>
